import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '@bedrock-rdk/constants';
import { t } from '../../i18n';
import { pauseAdsDisplaying, pauseAdsError } from '../../../logic/player/player.constants';
import { Pill } from '../interface/pill/pill.component';
import { cover } from '../../tornado/utils/textures';
import { getImageLoadTimeout, track } from './pauseAds.utils';
import { defaultContext, getInitialStyle } from './pauseAds.style';
import { DISPLAY_SIGNAL, HIDE_SIGNAL, IMAGE_LOAD_ERROR_CODE } from './pauseAds.constants';
import connect from './pauseAds.connector';

export class PauseAds extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Image: {},
        AdLabel: {
          type: Pill,
          title: t('player.advertisement'),
        },
        Resume: {
          type: Pill,
          title: t('player.pauseAdMessage'),
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._adAvailable = false;
    this._imageAvailable = false;
    this._style = defaultContext;
    this._canClose = false;
    this._shownCreative = null;
    this._creatives = [];
    this._creativeIndex = 0;
  }

  async _attach() {
    this._unsubscribe = connect(this);

    const image = this.tag('Image');

    image.on('txLoaded', () => {
      const creative = this._getCreative() || {};

      this._clearLoadTimeout();
      this._imageAvailable = true;
      this.tag('AdLabel').visible = true;
      this.tag('Resume').visible = true;

      if (!this.visible) {
        return;
      }

      Store.dispatch(pauseAdsDisplaying());
      this.signal(DISPLAY_SIGNAL);
      track(creative.impression);
      this._shownCreative = creative;

      if (Number.isFinite(this._maxDisplayDuration)) {
        this._displayTimeout = setTimeout(() => {
          this.signal(HIDE_SIGNAL);
        }, this._maxDisplayDuration * 1000);
      }

      if (Number.isFinite(this._closeActionDelay)) {
        this._closeActionTimeout = setTimeout(() => {
          this._canClose = true;
        }, this._closeActionDelay * 1000);
      }

      const { viewable } = creative;
      if (viewable) {
        this._viewableDelayTimeout = setTimeout(
          () => {
            track(viewable.beacons);
          },
          Number.isFinite(viewable.delay) ? 1000 * viewable.delay : 0,
        );
      }
    });

    image.on('txError', () => {
      this._handleCreativeError();
    });
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._creatives = [];
    this._creativeIndex = 0;
    this._clearLoadTimeout();
    clearTimeout(this._viewableDelayTimeout);
    clearTimeout(this._displayTimeout);
    clearTimeout(this._closeActionTimeout);
    this._viewableDelayTimeout = null;
    this._displayTimeout = null;
    this._closeActionTimeout = null;

    this.tag('Image').removeAllListeners('txLoaded');
    this.tag('Image').removeAllListeners('txError');
  }

  _inactive() {
    // NOTE: the "close" event should be fired on resume, on quit, or on close-button click
    if (this._shownCreative) {
      track(this._shownCreative.close);
      this._shownCreative = null;
    }
  }

  _handleBack() {
    if (!this._canClose) {
      return false;
    }

    this._canClose = false;
    this.signal(HIDE_SIGNAL);

    return true;
  }

  get isAvailable() {
    return this._adAvailable;
  }

  get isDisplaying() {
    return this.visible && this._imageAvailable;
  }

  set adRequestData(value) {
    this._adAvailable = Boolean(value);
    const params = value?.brParams || {};
    this._maxDisplayDuration = params.maxDisplayDuration;
    this._closeActionDelay =
      params.closeActionEnabled && Number.isFinite(params.closeActionDelay) ? params.closeActionDelay : null;
  }

  set ad(value) {
    this.tag('AdLabel').visible = false;
    this.tag('Resume').visible = false;
    this._clearLoadTimeout();

    this._imageAvailable = false;
    this._canClose = false;
    this.tag('Image').src = null;

    this._creatives = (value?.creatives || []).filter(creative => creative?.image);
    this._creativeIndex = 0;

    this._loadCreative();
  }

  _getCreative() {
    return this._creatives[this._creativeIndex];
  }

  _clearLoadTimeout() {
    clearTimeout(this._loadTimeout);
    this._loadTimeout = null;
  }

  _loadCreative() {
    const creative = this._getCreative();

    // the creative is only loaded while visible, a hidden one is resumed when shown again
    if (!creative || !this.visible) {
      return;
    }

    const image = this.tag('Image');
    const { screenSize } = this._style;

    this._clearLoadTimeout();
    this._loadTimeout = setTimeout(() => this._handleCreativeError(), getImageLoadTimeout() * 1000);

    image.texture = cover(creative.image, SCREEN_WIDTH[screenSize], SCREEN_HEIGHT[screenSize]);
    // force the ads image to be loaded in order to know in advance if the image is available
    image.loadTexture();
  }

  _handleCreativeError() {
    const failed = this._getCreative();

    this._clearLoadTimeout();
    if (!failed) {
      return;
    }

    track(failed.error?.map(value => value.replace(/\[ERRORCODE\]/g, IMAGE_LOAD_ERROR_CODE)));
    this._creativeIndex += 1;

    if (this._getCreative()) {
      this._loadCreative();

      return;
    }

    this._imageAvailable = false;
    this.tag('AdLabel').visible = false;
    this.tag('Resume').visible = false;
    this.tag('Image').src = null;
    Store.dispatch(pauseAdsError());
  }

  get visible() {
    return super.visible;
  }

  set visible(value) {
    clearTimeout(this._viewableDelayTimeout);
    clearTimeout(this._displayTimeout);
    clearTimeout(this._closeActionTimeout);
    this._viewableDelayTimeout = null;
    this._displayTimeout = null;
    this._closeActionTimeout = null;

    if (!value) {
      this._clearLoadTimeout();
      // a forced load is not cancelled by Lightning when the element gets disabled
      this.tag('Image').texture?.source?.cancel();
    }

    super.visible = value;

    // the outcome of an already settled load is emitted again on enabling, otherwise the load is resumed
    if (value && !this._imageAvailable && !this._loadTimeout) {
      this._loadCreative();
    }
  }
}
